"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { env } from "@/lib/environment";

interface TVScreenProps {
  platforms: Array<{
    name: string;
    color: string;
    iconUrl: string;
  }>;
}

export function TVScreen({ platforms }: TVScreenProps) {
  const [currentPlatform, setCurrentPlatform] = useState(platforms[0]?.name || "Chrome");
  const swiperRef = useRef<any>(null);

  // Define background colors for each platform (matching your existing code)
  const bgColors: Record<string, string> = {
    LGwebOS: "#FF3B30", // Red
    Android: "#34A853", // Green
    Chrome: "#4285F4", // Blue
    macOS: "#000000", // Black
    FireTV: "#FF9900", // Orange
    Windows: "#0078D7", // Windows Blue
    RaspberryPi: "#A71D31", // Raspberry Pi Red
  };

  useEffect(() => {
    // Initialize Swiper when component mounts
    const initSwiper = async () => {
      const { Swiper } = await import("swiper");
      const { Autoplay } = await import("swiper/modules");

      if (swiperRef.current) {
        const swiper = new Swiper(".mySwiper", {
          loop: true,
          slidesPerView: 4,
          spaceBetween: 20,
          centeredSlides: true,
          initialSlide: 0, // Always start from the first slide
          autoplay: {
            delay: 3000,
            disableOnInteraction: false,
          },
          modules: [Autoplay],
          on: {
            init: () => {
              // Set initial platform when Swiper initializes
              const activeSlide = document.querySelector(".swiper-slide-active");
              if (activeSlide) {
                const initialText = activeSlide.getAttribute("data-text");
                if (initialText) {
                  setCurrentPlatform(initialText);
                }
              }
            },
            slideChangeTransitionEnd: () => {
              const activeSlide = document.querySelector(".swiper-slide-active");
              if (activeSlide) {
                const newText = activeSlide.getAttribute("data-text");
                if (newText) {
                  setCurrentPlatform(newText);
                }
              }
            },
          },
          breakpoints: {
            0: {
              slidesPerView: 3,
            },
            640: {
              slidesPerView: 3,
            },
            768: {
              slidesPerView: 3,
            },
            1024: {
              slidesPerView: 4,
            },
          },
        });

        // Add click event listeners to slides
        document.querySelectorAll(".swiper-slide").forEach((slide, index) => {
          slide.addEventListener("click", () => {
            swiper.slideToLoop(index);
            setTimeout(() => {
              const activeSlide = document.querySelector(".swiper-slide-active");
              if (activeSlide) {
                const newText = activeSlide.getAttribute("data-text");
                if (newText) {
                  setCurrentPlatform(newText);
                }
              }
            }, 300);
          });
        });
      }
    };

    initSwiper();
  }, []);

  return (
    <div className="w-full">
      {/* TV Screen */}
      <div id="tv-container" className="relative mb-8 flex flex-col items-center">
        {/* TV Frame */}
        <div className="relative w-96 h-56">
          <Image
            src={`${env.NEXT_PUBLIC_CDN_URL}/images/digital-signage-player/FRAME-TV.K0_EY59vPujx.webp`}
            alt="TV Frame"
            fill
            className="object-cover"
            priority
            fetchPriority="high"
          />
          {/* Screen Content */}
          <div
            id="tv-content"
            className="absolute flex items-center justify-center transition-all duration-500 w-[94%] h-[90%] top-[2%] left-[3%]"
            style={{
              backgroundColor: bgColors[currentPlatform] || "#222222",
            }}
          >
            <span id="tv-text" className="text-white font-bold text-3xl">
              {currentPlatform}
            </span>
          </div>
        </div>
      </div>

      {/* Swiper Navigation */}
      <div className="swiper mySwiper mt-3 w-full" ref={swiperRef}>
        <div className="swiper-wrapper">
          {platforms.map((platform, index) => (
            <div
              key={platform.name}
              className="swiper-slide flex flex-col bg-gray-100 py-4 rounded-lg justify-center items-center gap-2 cursor-pointer scale-95"
              data-text={platform.name}
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto">
                <Image
                  src={platform.iconUrl}
                  alt={platform.name}
                  width={30}
                  height={30}
                  className="w-8 h-8 mx-auto"
                  fetchPriority={index < 4 ? "high" : "auto"}
                  priority={index < 4}
                />
              </div>
              <p className="text-xs font-medium text-center leading-tight mt-2">{platform.name}</p>
            </div>
          ))}
        </div>
      </div>
      <style jsx global>{`
        .mySwiper .swiper-slide {
          transition: transform 300ms ease;
        }
        .mySwiper .swiper-slide-active {
          --tw-scale-x: 1.10;
          --tw-scale-y: 1.10;
        }
      `}</style>
    </div>
  );
}
